import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { WhereClauseField } from '@modules/common/components/WhereClauseField';
import { EditableCompareFieldName } from '@modules/quantify/components/QuantifyQueryBuilder/Fields/EditableCompareFieldName/EditableCompareFieldName';
import { DEFAULT_COMPARE_FILTER_A_NAME } from '@modules/quantify/constants';
import { useFetchQuantifyReport } from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  isValidQuery,
  trackQuantifyCompareEvents,
} from '@modules/quantify/utils';
import { QueryBuilderStoreProvider } from '@modules/queryBuilder/store/QueryBuilderStoreProvider';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { RestartAltRounded } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import {
  EmptyWhereClause,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';

const CompareFilterAContent = () => {
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const whereClauseA = useQuantifyStore(({ query }) => query?.whereClause);
  const isValidQueryState = useQuantifyStore(({ query }) =>
    isValidQuery(query),
  );

  const compareFilterAName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.baseQueryName || DEFAULT_COMPARE_FILTER_A_NAME,
  );

  const qbDispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const rootID = useQueryBuilderStore(({ query }) => query.rootID);
  const nodesByID = useQueryBuilderStore(({ query }) => query.nodes);

  const fetchReportData = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);

  const analysisLink = useAnalysisStore(({ href }) => href);

  useEffect(() => {
    if (shouldFetchReportData && isValidQueryState) {
      setShouldFetchReportData(false);
      fetchReportData();
    }
  }, [fetchReportData, isValidQueryState, shouldFetchReportData]);

  useEffect(() => {
    dispatch({
      type: QuantifyActionType.SET_MISC,
      payload: {
        compareQBFieldA: {
          dispatch: qbDispatch,
          rootID,
          nodesByID,
        },
      },
    });

    return () => {
      dispatch({
        type: QuantifyActionType.SET_MISC,
        payload: {
          compareQBFieldA: null,
        },
      });
    };
  }, [dispatch, nodesByID, qbDispatch, rootID]);

  const onChange = useCallback(
    ({ whereClause }: { whereClause: WhereClause | null }) => {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          whereClause: whereClause ?? undefined,
          formulaQuery: {
            denominatorWhereClause: whereClause ?? undefined,
          },
          shouldShowTopDataPoints: true,
          visibleDataPoints: [],
        },
      });

      if (!whereClause) {
        dispatch({
          type: QuantifyActionType.SET_QUERY,
          payload: {
            compareMetadata: {
              baseQueryName: '',
            },
          },
        });
      }

      setShouldFetchReportData(true);
      trackQuantifyCompareEvents({
        eventName: 'Quantify Compare - Filter A Changed',
        link: analysisLink,
      });
    },
    [dispatch, analysisLink],
  );

  const onReset = () => {
    onChange({ whereClause: null });
    trackQuantifyCompareEvents({
      eventName: 'Quantify Compare - Reset Filter Icon Button Clicked',
      link: analysisLink,
    });
  };

  const onChangeFilterAName = (filterAName: string) => {
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        compareMetadata: {
          baseQueryName: filterAName,
        },
      },
    });
    trackQuantifyCompareEvents({
      eventName: 'Quantify Compare - Filter A renamed',
      filterName: filterAName,
      link: analysisLink,
    });
  };

  const isFilterAEmpty =
    !whereClauseA || isEqual(EmptyWhereClause, whereClauseA);

  return (
    <Stack spacing={1} pb={1} data-testid="quantify-compare-filter-field-a">
      <Stack
        spacing={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <EditableCompareFieldName
          initialFieldName={compareFilterAName}
          onChange={onChangeFilterAName}
        />
        <IconButton
          size="small"
          color="primary"
          disabled={isFilterAEmpty}
          onClick={onReset}
        >
          <RestartAltRounded fontSize="small" />
        </IconButton>
      </Stack>
      <WhereClauseField
        whereClause={whereClauseA}
        onChange={onChange}
        modalTitle={`Add ${compareFilterAName}`}
        label={null}
        helperTextNode={
          isFilterAEmpty ? (
            <Typography variant="caption" color="info.main">
              Filter cannot be left empty
            </Typography>
          ) : null
        }
        modalTestId="quantify-compare-filter-field-a-modal"
      />
    </Stack>
  );
};

export const CompareFilterA = () => {
  return (
    <QueryBuilderStoreProvider>
      <CompareFilterAContent />
    </QueryBuilderStoreProvider>
  );
};
